import { DEFAULT_SET_BY_TYPE, getWorkoutTypeConfig } from "../constants";
import { calculateSwimTemplateMeters } from "../domain/swimming/calculations";

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const getBlocks = (template = {}) => {
    const sessions = template.sessions || template.workouts || [];
    return sessions.flatMap(session => session.blocks || []);
};

export const inferWorkoutType = (template = {}) => {
    if (template.workoutType) return template.workoutType;
    const firstBlock = getBlocks(template)[0];
    return firstBlock?.workoutType || "strength";
};

export const getTemplateSummary = (template = {}) => {
    const workoutType = inferWorkoutType(template);
    const typeConfig = getWorkoutTypeConfig(workoutType);
    const sessions = template.sessions || template.workouts || [];
    const blocks = getBlocks(template);
    const sets = blocks.flatMap(block => block.sets || []);
    const sessionsCount = sessions.length;

    if (workoutType === "swimming") {
        const totalMeters = calculateSwimTemplateMeters(template);
        const intensityCount = sets.filter(set => set.intensity).length;

        return {
            workoutType,
            typeConfig,
            sessionsCount,
            blocksCount: blocks.length,
            mainMetric: totalMeters > 0 ? `${totalMeters.toLocaleString("pt-BR")} m` : "Sem metragem",
            secondaryMetric: intensityCount > 0 ? `${intensityCount} zonas definidas` : "Sem zonas",
        };
    }

    if (workoutType === "running") {
        const totalKm = sets.reduce((sum, set) => (
            sum + (toNumber(set.reps) || 1) * toNumber(set.distance)
        ), 0);
        const paceCount = sets.filter(set => set.pace).length;

        return {
            workoutType,
            typeConfig,
            sessionsCount,
            blocksCount: blocks.length,
            mainMetric: totalKm > 0 ? `${totalKm.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} km` : "Sem distância",
            secondaryMetric: paceCount > 0 ? `${paceCount} paces definidos` : "Sem pace",
        };
    }

    if (workoutType === "strength") {
        const exercises = sets.filter(set => set.exercise).length;
        const muscleGroups = new Set(sets.map(set => set.muscleGroup).filter(Boolean));

        return {
            workoutType,
            typeConfig,
            sessionsCount,
            blocksCount: blocks.length,
            mainMetric: `${exercises || sets.length} exercícios`,
            secondaryMetric: muscleGroups.size > 0 ? `${muscleGroups.size} grupos musculares` : "Sem grupos",
        };
    }

    return {
        workoutType,
        typeConfig,
        sessionsCount,
        blocksCount: blocks.length,
        mainMetric: `${sets.length} movimentos`,
        secondaryMetric: `${blocks.length} blocos`,
    };
};

const normalizeSetsForWorkoutType = (sets = [], workoutType = "strength") => {
    const defaultSet = DEFAULT_SET_BY_TYPE[workoutType] || {};

    return sets.map((set, index) => {
        const setType = set?.type;
        const incompatibleType = setType && setType !== defaultSet.type;

        if (incompatibleType) {
            return {
                id: set.id || `${Date.now()}_${index}`,
                ...defaultSet,
            };
        }

        return set;
    });
};

const normalizeSessionsForWorkoutType = (template = {}, workoutType = "strength") => (
    (template.sessions || template.workouts || []).map(session => ({
        ...session,
        sessionType: workoutType,
        blocks: (session.blocks || []).map(block => ({
            ...block,
            workoutType,
            sets: normalizeSetsForWorkoutType(block.sets || [], workoutType),
        })),
    }))
);

export const normalizeTemplateWorkoutType = (template = {}, workoutType = "strength") => {
    const sessions = normalizeSessionsForWorkoutType(template, workoutType);

    return {
        ...template,
        workoutType,
        sessions,
        workouts: sessions,
        blocks: [],
    };
};
